'use client';

/**
 * MediaBlock - Unified media rendering
 * 
 * Renders images, videos, and embeds with configurable styling.
 */

import React, { useMemo } from 'react';
import { MediaBlockConfig, AnimationConfig } from '../types';
import { SmoothReveal } from '@/components/shared/SmoothReveal';
import S3FilePreview from '@/components/shared/S3FilePreview';
import { isVideoSource } from '@/lib/video-utils';
import { getBrandColor } from '@/config/brand-colors';
import { Play } from 'lucide-react';

type MediaPriority = 'CRITICAL' | 'HIGH' | 'NORMAL' | 'LOW' | 'BACKGROUND' | boolean;

interface MediaBlockProps {
  config?: MediaBlockConfig;
  animation?: AnimationConfig;
  className?: string;
  priority?: MediaPriority;
}

const aspectRatioClasses: Record<string, string> = {
  video: 'aspect-video',
  square: 'aspect-square',
  portrait: 'aspect-[3/4]',
  auto: '',
};

export function MediaBlock({ config, animation, className = '', priority = 'NORMAL' }: MediaBlockProps) {
  if (!config || config.type === 'none' || !config.src) return null;

  const isVideo = useMemo(() => {
    if (config.type === 'video') return true;
    return isVideoSource(config.src || '');
  }, [config.type, config.src]);

  const aspectClass = config.aspectRatio 
    ? (aspectRatioClasses[config.aspectRatio] || '')
    : aspectRatioClasses.video;

  const roundedClass = config.rounded === true 
    ? 'rounded-2xl' 
    : typeof config.rounded === 'string' 
      ? config.rounded 
      : '';

  const shadowClass = config.shadow === true 
    ? 'shadow-xl' 
    : typeof config.shadow === 'string' 
      ? config.shadow 
      : '';

  const containerClasses = `relative overflow-hidden ${aspectClass} ${roundedClass} ${shadowClass} ${className}`.trim();

  const delay = animation?.delay || 0;

  return (
    <SmoothReveal 
      delay={delay}
      variant="fade-up"
      className={containerClasses}
    >
      <div className="absolute inset-0 w-full h-full">
        <S3FilePreview
          src={config.src}
          alt={config.alt || 'Media'}
          className="w-full h-full"
          style={{ 
            width: '100%', 
            height: '100%', 
            objectFit: config.objectFit || 'cover',
            objectPosition: config.objectPosition || 'center',
          }}
          autoplay={isVideo ? (config.autoplay ?? true) : false}
          loop={isVideo ? (config.loop ?? true) : false}
          muted={isVideo ? (config.muted ?? true) : false}
          controls={isVideo ? (config.controls ?? false) : false}
          playsInline={isVideo}
          disablePreviewModal={false}
          disableSkeleton={false}
          // Priority controls preload strategy automatically
          priority={priority}
        />

        {/* Play button overlay for videos */}
        {isVideo && config.showPlayButton && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
            <div 
              className="w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110"
              style={{ 
                backgroundColor: getBrandColor('primaryBlue') + '80',
                boxShadow: '0 0 40px rgba(0, 51, 153, 0.4)'
              }}
            >
              <Play className="w-6 h-6 md:w-8 md:h-8 text-white ml-1" fill="white" />
            </div>
          </div>
        )}
      </div>
    </SmoothReveal>
  );
}

export default MediaBlock;
